import { useAnalytics } from '../../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../../constants';
import { useTranslation } from "react-i18next";
import {
  IconFavorite,
  IconInfo,
  IconDemo,
  IconOpenInNew,
  IconFavoriteBorder,
  IconSettings,
  MenuListItem,
} from "cdk-radial";
import { useRef, useState } from "react";
import Card from "./Card";
import LearnMoreDialog from './LearnMoreDialog';
import userProfileStore from '../../core/UserProfileStore';
import * as Styles from './styles';
import AdoptEaseApplicationDialog from '../../adopt-ease/AdoptEaseApplicationDialog';
import AdoptEaseCampaignDialog from '../../adopt-ease/AdoptEaseCampaignDialog';
import { AdoptEaseCampaignAction, IAdoptEaseCampaign } from '../../adopt-ease/AdoptEaseCampaignInterface';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { isBlockedByOS } from '../../../util/osUtils';

interface Props {
  id: string;
  isEnabled: boolean;
  isIconRequired: boolean;
  warningMessage?:any;
  requiredOS?: string[];
  isFavorite?: boolean;
  description?: string;
  name: string;
  iconColor: string;
  iconText: string;
  url: string;
  cdkuUrl?: string;
  onClick?: Function;
  onCdkUniversityClick?: Function;
  onOpenInNewTabClick?: Function;
  onFavoriteChange?: Function;
  isRestricted?: boolean;
  restrictedTooltipMsg?: string;
  openInNewTab?: boolean;
  showTryNewAdoptEaseButton?: Function;
  onTryNewAdoptEaseClick?:Function;
  showTryNewAdoptEaseCampaign?: (id: string) => IAdoptEaseCampaign | undefined;
  showNewIndicator?:boolean;
  learnMoreDescription?:string;
  menu?:any;
  isDefault?: boolean;
  onSetDefault?: (id: string, name: string, isDefault: boolean) => void;
  showStartupOption?: boolean;
  isMobileApp?: boolean;
}
export interface CardMenu {
  key: string;
  label: string;
  icon?: any;
  onClick: Function;
}

export const getDefaultMenuOptions = (
  t: any,
  isFavorite: boolean,
  onFavoriteChange: Function,
  onCdkUClick: Function,
  onLearnMoreClick: Function,
  onOpenInNewTabClick: Function,
  cdkuUrl?: string,
  openInNewTab?:boolean,
): CardMenu[] => {
  const Icon = isFavorite ? IconFavorite : IconFavoriteBorder;
  const options: CardMenu[] = [
    {
      label: isFavorite
        ? t("baseCard.menu.removeFromMyUnify")
        : t("baseCard.menu.addToMyUnify"),
      key: "MY_UNIFY",
      icon: <Icon />,
      onClick: onFavoriteChange
    },
    {
      label: t("baseCard.menu.learnMore"),
      key: "LEARN_MORE",
      icon: <IconInfo />,
      onClick: onLearnMoreClick
    },
  ];
  if (cdkuUrl) {
    options.push({
      label: t("baseCard.menu.cdku"),
      key: "CDKU",
      icon: <IconDemo />,
      onClick: onCdkUClick
    });
  }
  if(openInNewTab){
    options.push(
      { label: t('baseCard.menu.openNewTab'), 
        key:'OPEN_IN_TAB',  
        icon: <IconOpenInNew />,
        onClick: onOpenInNewTabClick 
    });
  }

  return options;
};


export default function BaseCard({
  id,
  isIconRequired,
  isFavorite,
  isEnabled,
  warningMessage,
  requiredOS,
  description,
  name,
  iconText,
  iconColor,
  cdkuUrl,
  url,
  onClick,
  onCdkUniversityClick,
  onOpenInNewTabClick,
  onFavoriteChange,
  isRestricted,
  restrictedTooltipMsg,
  openInNewTab ,
  onTryNewAdoptEaseClick,
  showTryNewAdoptEaseButton,
  showTryNewAdoptEaseCampaign,
  showNewIndicator,
  learnMoreDescription,
  menu,
  isDefault = false,
  onSetDefault,
  showStartupOption = false,
  isMobileApp = false,
}: Props) {
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const menuRef = useRef();
  const [learnMoreData,setLearnMoreData] = useState({});
  const [showLearnMoreDialog,setShowLearnMoreDialog] = useState(false);
  const [showAdoptEaseDialog,setShowAdoptEaseDialog] = useState(false);
  const [adoptEaseCampaign,setAdoptEaseCampaign] = useState<IAdoptEaseCampaign | undefined>();
  const {locale} = userProfileStore();
  const startupAppFlag = useFeatureFlag('UNIFY_STARTUP_APP');
  const osBlocked = isBlockedByOS(requiredOS);
  const handleFavoriteChange = () => {
    onFavoriteChange && onFavoriteChange(id,name,!isFavorite);
  };

  const handleCdkUClick = () => {
    onCdkUniversityClick && onCdkUniversityClick(id,name);
  };

  const onLearnMoreClick = () =>{
    setLearnMoreData({
      iconColor,
      iconText,
      name,
      description:learnMoreDescription,
    });
    setShowLearnMoreDialog(true);
  }

  const onHandleOpenInNewTabClick = () =>{
    if (id && url) {
      trackEvent(AMPLITUDE_EVENTS.OPEN_IN_NEW_TAB, {
        'Application': name
      });
      onOpenInNewTabClick && onOpenInNewTabClick(id, name, url);
    }
  };

  const menuOptions = getDefaultMenuOptions(
      t, 
      isFavorite || false,
      handleFavoriteChange,
      handleCdkUClick,
      onLearnMoreClick,
      onHandleOpenInNewTabClick,
      cdkuUrl,
      openInNewTab && !osBlocked,);

  const startupMenuItem: CardMenu[] = (showStartupOption && Boolean(startupAppFlag) && !osBlocked) ? [{
    label: isDefault ? t('baseCard.startupApp.removeLabel') : t('baseCard.startupApp.setLabel'),
    key: 'DEFAULT_STARTUP',
    icon: <IconSettings />,
    onClick: () => onSetDefault?.(id, name, isDefault),
  }] : [];

  const onCardClick = (e: MouseEvent) => {
    onClick && onClick({ id, name, url, icon: iconColor, iconText });
  };

  const onHandleTryNewAdoptEaseClick = (e: MouseEvent) => {
    setShowAdoptEaseDialog(true);
    onTryNewAdoptEaseClick && onTryNewAdoptEaseClick(e);
  };

  const onHandleTryNewAdoptEaseCampaignClick = (campaign: IAdoptEaseCampaign) => {
    trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_CAMPAIGN, { Action: AdoptEaseCampaignAction.OPEN_MODAL, Item: id });
    setAdoptEaseCampaign(campaign);
  };

  return (
    <>
      <Card
        id={id}
        isIconRequired={isIconRequired}
        iconColor={iconColor}
        iconText={iconText}
        onOpenClick={(e) => onCardClick(e as unknown as MouseEvent)}
        name={name}
        isEnabled={isEnabled}
        warningMessage={warningMessage}
        requiredOS={requiredOS}
        menuRef={menuRef}
        description={description}
        isRestricted={isRestricted}
        restrictedTooltipMsg={restrictedTooltipMsg}
        onTryNewAdoptEaseClick={onHandleTryNewAdoptEaseClick}
        showTryNewAdoptEaseButton={showTryNewAdoptEaseButton}
        showTryNewAdoptEaseCampaign={showTryNewAdoptEaseCampaign}
        onTryNewAdoptEaseCampaignClick={onHandleTryNewAdoptEaseCampaignClick}
        showNewIndicator={showNewIndicator}
        locale={locale}
        isMobileApp={isMobileApp}
      />
      <Styles.StyledMenuContainer labelRef={menuRef} dataTestId="bc-menu-more">
        {[...menuOptions, ...(menu || []), ...startupMenuItem]
          .filter(Boolean)
          .map((option: any, ind: any) => (
            <MenuListItem
              key={option.id || option.label || `menu-item-${ind}`}
              isSelected={false}
              dataTestId={`lm-mn-${ind}`}
              graphicContent={option?.icon}
              onClick={(e: MouseEvent) => option?.onClick?.()}
            >
              {option?.label}
            </MenuListItem>
          ))
        }
      </Styles.StyledMenuContainer>

      <AdoptEaseApplicationDialog
              isOpen={showAdoptEaseDialog}
              onClose={() => setShowAdoptEaseDialog(false)}
              iconColor={iconColor}
              iconText={iconText}
              name={name}
              description={description || ''}
              cdkUUrl={cdkuUrl ? cdkuUrl : ''}
              id={id ? id : ''}
              onOpenClick={onCardClick}
            />

      <AdoptEaseCampaignDialog
              isOpen={!!adoptEaseCampaign}
              onClose={() => setAdoptEaseCampaign(undefined)}
              iconColor={iconColor}
              iconText={iconText}
              name={name}
              id={id ? id : ''}
              campaign={adoptEaseCampaign}
            />

      <LearnMoreDialog learnMoreData={learnMoreData}
        isOpen={showLearnMoreDialog} 
        onClose={()=>setShowLearnMoreDialog(false)}></LearnMoreDialog>

    </>
  );
}

